import React, { useState, useEffect, useRef } from "react";
import { Card } from "flowbite-react";
import { hasTimePassed } from "@/lib/dateUtils";
import { Progress } from "@/components/ui/progress";
import { PrayerTimesResponse, calculateProgress } from '../lib/prayerTimesAPI';
import { useTranslation } from 'react-i18next';

interface CountdownTimerProps {
  prayerTimesData: PrayerTimesResponse | null;
}

const CountdownTimer = ({ prayerTimesData }: CountdownTimerProps) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [nextPrayer, setNextPrayer] = useState<string>("fajr");
  const [progressValue, setProgressValue] = useState<number>(0);
  
  useEffect(() => {
    if (!prayerTimesData) return;
    
    const calculateTimeToNextPrayer = () => {
      const now = new Date();
      const prayers = {
        fajr: prayerTimesData.prayerTimes.fajr,
        dhuhr: prayerTimesData.prayerTimes.dhuhr,
        asr: prayerTimesData.prayerTimes.asr,
        maghrib: prayerTimesData.prayerTimes.maghrib,
        isha: prayerTimesData.prayerTimes.isha
      };
      
      // Find the next prayer time
      const currentTime = now.getHours() * 60 + now.getMinutes();
      let nextPrayerName = "fajr";
      let nextPrayerTime = 24 * 60; // Default to next day's Fajr
      
      for (const [prayer, time] of Object.entries(prayers)) {
        const [hours, minutes] = time.split(":").map(Number);
        const prayerTimeInMinutes = hours * 60 + minutes;
        
        if (prayerTimeInMinutes > currentTime && prayerTimeInMinutes < nextPrayerTime) {
          nextPrayerTime = prayerTimeInMinutes;
          nextPrayerName = prayer;
        }
      }
      
      setNextPrayer(nextPrayerName);
      
      // Calculate time remaining
      let targetTime;
      if (nextPrayerName === "fajr" && nextPrayerTime === 24 * 60) {
        // If next prayer is tomorrow's Fajr
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const [hours, minutes] = prayers.fajr.split(":").map(Number);
        tomorrow.setHours(hours, minutes, 0, 0);
        targetTime = tomorrow;
      } else {
        // Today's prayer
        const [hours, minutes] = prayers[nextPrayerName].split(":").map(Number);
        const target = new Date(now);
        target.setHours(hours, minutes, 0, 0);
        targetTime = target;
      }
      
      // Calculate difference
      const diff = targetTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0 };
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return { hours, minutes, seconds };
    };
    
    // Update progress bar
    const updateProgressBar = () => {
      if (prayerTimesData) {
        setProgressValue(calculateProgress(prayerTimesData));
      }
    };
    
    // Initial calculations
    setTimeRemaining(calculateTimeToNextPrayer());
    updateProgressBar();
    
    // Update every second
    const timeInterval = setInterval(() => {
      setTimeRemaining(calculateTimeToNextPrayer());
    }, 1000);
    
    // Update progress bar every minute
    const progressInterval = setInterval(updateProgressBar, 60000);
    
    return () => {
      clearInterval(timeInterval);
      clearInterval(progressInterval);
    };
  }, [prayerTimesData]);
  
  if (!prayerTimesData) return null;
  
  return (
    <Card className="w-full max-w-md mx-auto overflow-hidden animate-fadeIn shadow-lg">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-2 sm:p-4 rounded-t-lg">
        <div className="flex items-center justify-center">
          <h2 className="text-base sm:text-xl font-bold">
            {t('timeUntil')} {t(nextPrayer)}
          </h2>
        </div>
      </div>
      
      <div className="p-3 sm:p-6 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30">
        <div className="grid grid-cols-3 gap-1 sm:gap-4 mb-3 sm:mb-6">
          <div className="flex flex-col items-center">
            <div className="text-xl sm:text-3xl font-bold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 rounded-lg p-2 sm:p-3 shadow-inner min-w-[60px] sm:min-w-[80px] text-center">
              {timeRemaining.hours.toString().padStart(2, '0')}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">{t('hours')}</div>
          </div>
          <div className="flex flex-col items-center">
            <div className="text-xl sm:text-3xl font-bold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 rounded-lg p-2 sm:p-3 shadow-inner min-w-[60px] sm:min-w-[80px] text-center">
              {timeRemaining.minutes.toString().padStart(2, '0')}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">{t('minutes')}</div>
          </div>
          <div className="flex flex-col items-center">
            <div className="text-xl sm:text-3xl font-bold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 rounded-lg p-2 sm:p-3 shadow-inner min-w-[60px] sm:min-w-[80px] text-center">
              {timeRemaining.seconds.toString().padStart(2, '0')}
            </div>
            <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">{t('seconds')}</div>
          </div>
        </div>
        
        <div className="mt-2 sm:mt-4">
          <Progress 
            value={progressValue} 
            className="h-1.5 sm:h-2 bg-purple-100 dark:bg-purple-900/30"
          />
          <div className="sr-only">
            {progressValue}% until {nextPrayer}
          </div>
          <div className="text-[10px] sm:text-sm text-center text-purple-700 dark:text-purple-300 mt-1 sm:mt-2">
            <span className="break-words">
              {t('nextPrayerTime')} <span className="font-medium">{t(nextPrayer)} {prayerTimesData?.prayerTimes[nextPrayer]}</span>
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CountdownTimer;
